import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft,
    Building2,
    Mail,
    Phone,
    MapPin,
    Star,
    Plus,
    Edit,
    FolderKanban,
    Receipt,
    FileText,
    User,
    Send,
    ExternalLink,
    Loader2,
} from "lucide-react";
import { cn, getStatusColor, statusLabels, formatDate, formatCurrency } from "../../lib/utils";

export const ClientDetailPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const { get, put, post } = useApi();
    
    const [client, setClient] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sendingPortalLink, setSendingPortalLink] = useState(false);
    const [editData, setEditData] = useState({});
    const [contactForm, setContactForm] = useState({
        name: "",
        email: "",
        phone: "",
        position: "",
        is_primary: false,
    });

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get(`/clients/${clientId}`),
            get(`/clients/${clientId}/contacts`),
            get("/projects"),
            get("/quotes"),
            get("/invoices"),
        ]);
        if (results[0].status === "fulfilled") {
            setClient(results[0].value);
            setEditData(results[0].value);
        } else {
            toast.error("Erreur lors du chargement du client");
            navigate("/clients");
            return;
        }
        if (results[1].status === "fulfilled") setContacts(results[1].value);
        if (results[2].status === "fulfilled") setProjects(results[2].value.filter(p => p.client_id === clientId));
        if (results[3].status === "fulfilled") setQuotes(results[3].value.filter(q => q.client_id === clientId));
        if (results[4].status === "fulfilled") setInvoices(results[4].value.filter(i => i.client_id === clientId));
        setLoading(false);
    }, [get, clientId, navigate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleUpdateClient = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await put(`/clients/${clientId}`, editData);
            toast.success("Client mis à jour");
            setEditDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await post(`/clients/${clientId}/contacts`, { ...contactForm, client_id: clientId });
            toast.success("Contact ajouté");
            setContactDialogOpen(false);
            setContactForm({ name: "", email: "", phone: "", position: "", is_primary: false });
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de l'ajout du contact");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSendEmail = async () => {
        if (!client?.email) {
            toast.error("Ce client n'a pas d'email");
            return;
        }
        toast.info("Fonctionnalité d'envoi d'email disponible depuis les factures et devis");
    };

    const handleSendPortalLink = async () => {
        if (!client?.email) {
            toast.error("Ce client n'a pas d'adresse email");
            return;
        }
        setSendingPortalLink(true);
        try {
            const result = await post(`/portal/magic-link/generate?client_id=${clientId}`);
            toast.success(`Lien d'accès envoyé à ${result.client_email}`);
        } catch (error) {
            toast.error(error.message || "Erreur lors de l'envoi du lien");
        } finally {
            setSendingPortalLink(false);
        }
    };

    // Calculate stats
    const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
    const pendingAmount = invoices.reduce((sum, inv) => {
        if (inv.status !== "paid" && inv.status !== "cancelled") {
            return sum + (inv.total - (inv.amount_paid || 0));
        }
        return sum;
    }, 0);
    const activeProjects = projects.filter(p => p.status === "in_progress").length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!client) return null;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <Button variant="ghost" onClick={() => navigate("/clients")} className="w-fit">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour aux clients
                </Button>

                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h1 className="font-heading font-bold text-2xl sm:text-3xl">{client.name}</h1>
                                <Badge className={getStatusColor(client.status)}>
                                    {statusLabels[client.status]}
                                </Badge>
                            </div>
                            {client.industry && (
                                <p className="text-muted-foreground">{client.industry}</p>
                            )}
                            {client.rating && (
                                <div className="flex items-center gap-1 mt-2">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            className={cn(
                                                "w-4 h-4",
                                                i < client.rating
                                                    ? "text-yellow-500 fill-yellow-500"
                                                    : "text-muted-foreground"
                                            )}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleSendPortalLink}
                            disabled={sendingPortalLink}
                            data-testid="send-portal-link-btn"
                        >
                            {sendingPortalLink ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <ExternalLink className="w-4 h-4 mr-2" />
                            )}
                            Accès portail
                        </Button>
                        <Button variant="outline" onClick={handleSendEmail}>
                            <Send className="w-4 h-4 mr-2" />
                            Contacter
                        </Button>
                        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="edit-client-btn">
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading">Modifier le client</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleUpdateClient} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <Label>Nom</Label>
                                            <Input
                                                value={editData.name || ""}
                                                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>Email</Label>
                                            <Input
                                                type="email"
                                                value={editData.email || ""}
                                                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>Téléphone</Label>
                                            <Input
                                                value={editData.phone || ""}
                                                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>Secteur</Label>
                                            <Input
                                                value={editData.industry || ""}
                                                onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>Statut</Label>
                                            <Select
                                                value={editData.status}
                                                onValueChange={(value) => setEditData({ ...editData, status: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="prospect">Prospect</SelectItem>
                                                    <SelectItem value="negotiation">En négociation</SelectItem>
                                                    <SelectItem value="active">Client actif</SelectItem>
                                                    <SelectItem value="loyal">Client fidèle</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Adresse</Label>
                                            <Input
                                                value={editData.address || ""}
                                                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Notes</Label>
                                            <Textarea
                                                value={editData.notes || ""}
                                                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                                            Annuler
                                        </Button>
                                        <Button type="submit" disabled={submitting}>
                                            {submitting ? "Sauvegarde..." : "Sauvegarder"}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">Chiffre d'affaires</p>
                        <p className="text-2xl font-bold font-mono text-green-600">{formatCurrency(totalRevenue)}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">En attente</p>
                        <p className="text-2xl font-bold font-mono text-orange-600">{formatCurrency(pendingAmount)}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">Projets actifs</p>
                        <p className="text-2xl font-bold font-mono">{activeProjects}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">Total projets</p>
                        <p className="text-2xl font-bold font-mono">{projects.length}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Contact Info & Contacts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Contact Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Informations</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {client.email && (
                            <div className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-muted-foreground" />
                                <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                                    {client.email}
                                </a>
                            </div>
                        )}
                        {client.phone && (
                            <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-muted-foreground" />
                                <a href={`tel:${client.phone}`} className="hover:underline">
                                    {client.phone}
                                </a>
                            </div>
                        )}
                        {client.address && (
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                                <span>{client.address}</span>
                            </div>
                        )}
                        {client.notes && (
                            <div className="pt-4 border-t">
                                <p className="text-sm text-muted-foreground mb-2">Notes</p>
                                <p className="text-sm">{client.notes}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Contacts List */}
                <Card className="lg:col-span-2">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">Contacts</CardTitle>
                        <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Nouveau contact</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleAddContact} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <Label>Nom *</Label>
                                            <Input
                                                value={contactForm.name}
                                                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label>Email</Label>
                                            <Input
                                                type="email"
                                                value={contactForm.email}
                                                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>Téléphone</Label>
                                            <Input
                                                value={contactForm.phone}
                                                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Poste</Label>
                                            <Input
                                                value={contactForm.position}
                                                onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                                            Annuler
                                        </Button>
                                        <Button type="submit" disabled={submitting}>
                                            {submitting ? "Ajout..." : "Ajouter"}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </CardHeader>
                    <CardContent>
                        {contacts.length === 0 ? (
                            <p className="text-center py-8 text-muted-foreground">Aucun contact</p>
                        ) : (
                            <div className="space-y-3">
                                {contacts.map((contact) => (
                                    <div key={contact.contact_id} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/50">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <User className="w-5 h-5 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium">{contact.name}</p>
                                            {contact.position && (
                                                <p className="text-sm text-muted-foreground">{contact.position}</p>
                                            )}
                                        </div>
                                        <div className="text-right text-sm">
                                            {contact.email && <p>{contact.email}</p>}
                                            {contact.phone && <p className="text-muted-foreground">{contact.phone}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Tabs: Projects, Quotes, Invoices */}
            <Tabs defaultValue="projects" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="projects">
                        <FolderKanban className="w-4 h-4 mr-2" />
                        Projets ({projects.length})
                    </TabsTrigger>
                    <TabsTrigger value="quotes">
                        <FileText className="w-4 h-4 mr-2" />
                        Devis ({quotes.length})
                    </TabsTrigger>
                    <TabsTrigger value="invoices">
                        <Receipt className="w-4 h-4 mr-2" />
                        Factures ({invoices.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="projects">
                    <Card>
                        <CardContent className="p-0">
                            {projects.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucun projet pour ce client</p>
                                    <Button className="mt-4" onClick={() => navigate("/projects")}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Créer un projet
                                    </Button>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {projects.map((project) => (
                                        <div
                                            key={project.project_id}
                                            className="flex items-center justify-between p-4 hover:bg-secondary/50 cursor-pointer"
                                            onClick={() => navigate(`/projects/${project.project_id}`)}
                                        >
                                            <div>
                                                <p className="font-medium">{project.name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDate(project.start_date)} - {formatDate(project.end_date)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge className={getStatusColor(project.status)}>
                                                    {statusLabels[project.status]}
                                                </Badge>
                                                {project.budget && (
                                                    <span className="font-mono text-sm">{formatCurrency(project.budget)}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="quotes">
                    <Card>
                        <CardContent className="p-0">
                            {quotes.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucun devis pour ce client</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {quotes.map((quote) => (
                                        <div key={quote.quote_id} className="flex items-center justify-between p-4">
                                            <div>
                                                <p className="font-mono font-medium">{quote.quote_number}</p>
                                                <p className="text-sm text-muted-foreground">{formatDate(quote.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge className={getStatusColor(quote.status)}>
                                                    {statusLabels[quote.status]}
                                                </Badge>
                                                <span className="font-mono font-medium">{formatCurrency(quote.total)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="invoices">
                    <Card>
                        <CardContent className="p-0">
                            {invoices.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucune facture pour ce client</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {invoices.map((invoice) => (
                                        <div key={invoice.invoice_id} className="flex items-center justify-between p-4">
                                            <div>
                                                <p className="font-mono font-medium">{invoice.invoice_number}</p>
                                                <p className="text-sm text-muted-foreground">{formatDate(invoice.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge className={getStatusColor(invoice.status)}>
                                                    {statusLabels[invoice.status]}
                                                </Badge>
                                                <div className="text-right">
                                                    <p className="font-mono font-medium">{formatCurrency(invoice.total)}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Payé: {formatCurrency(invoice.amount_paid || 0)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};
